import { useEffect, useState, JSX } from 'react';
import { useParams } from 'react-router-dom';

import { AssessmentLinkData } from 'api/assessments/types';
import AssessmentProxy from 'api/assessments/assessmentProxy';
import FlexContainer from 'global_elements/Layouts/FlexContainer';
import { DisplayVariant, AlignVariant, JustifyVariant } from 'global_elements/Layouts/FlexContainer/variants';
import PageLayout from 'global_elements/Layouts/PageLayout';
import { PageLayoutVariant } from 'global_elements/Layouts/PageLayout/variants';
import InlineText from 'global_elements/Text/InlineText';
import { FontColors, FontSizes } from 'global_elements/Text/variants';
import { Languages } from 'constants/languages';
import { useTranslation } from 'react-i18next';
import { anonymousAssessmentTranslationKeys } from './index.i18n';

import AssessmentVerify from './AssessmentVerify';
import AssessmentForm from './AssessmentForm';

import './styles.scss';

interface UriParams {
  assessmentLinkHash: string;
  languageId: string;
}

export function AssessmentPage(): JSX.Element {
  const { assessmentLinkHash, languageId } = useParams<UriParams>();
  const { t, i18n } = useTranslation('translation', { keyPrefix: 'anonymousAssessment' });
  const [assessmentLinkData, setAssessmentLinkData] = useState<AssessmentLinkData[] | null>();
  const [loading, setLoading] = useState<boolean>(true);
  const [expired, setExpired] = useState<boolean>(false);

  useEffect(() => {
    if (languageId) {
      i18n.changeLanguage(languageId === Languages.Spanish.toString() ? 'es' : 'en');
    }
    AssessmentProxy.verifyLinkHash(assessmentLinkHash)
      .then(() => {
        setExpired(false);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setExpired(true);
      });
  }, []);

  const onVerified = (data: AssessmentLinkData[]): void => {
    setAssessmentLinkData(data);
  };

  if (loading || expired) {
    return (
      <PageLayout layout={PageLayoutVariant.PADDED} testText="Assessment Page" hideHeader>
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="assessment-page-container">
          {loading && <InlineText text={t(anonymousAssessmentTranslationKeys.loading)} fontColor={FontColors.PRIMARY} fontSize={FontSizes.SUPER_EXTRA_LARGE} />}
          {expired && <InlineText text={t(anonymousAssessmentTranslationKeys.linkExpired)} fontColor={FontColors.PRIMARY} fontSize={FontSizes.SUPER_EXTRA_LARGE} />}
        </FlexContainer>
      </PageLayout>
    );
  }

  if (!assessmentLinkData) {
    return <AssessmentVerify assessmentLinkHash={assessmentLinkHash} onVerified={onVerified} />;
  }

  return <AssessmentForm assessmentLinkHash={assessmentLinkHash} assessmentLinkData={assessmentLinkData} />;
}
